/**
 * Model name formatting utilities
 * Fixes concatenated names like "Textembedding Gecko" → "Text Embedding Gecko"
 */

/**
 * Formats a model display name by adding spaces between camelCase/concatenated words
 * Examples:
 * - "Textembedding Gecko" → "Text Embedding Gecko"
 * - "Multimodalembedding" → "Multimodal Embedding"
 * - "Imagegeneration" → "Image Generation"
 */
export function formatModelName(name: string): string {
  if (!name) return '';

  return (
    name
      // Insert space between lowercase and uppercase letters (camelCase)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Fix specific known patterns
      .replace(/Text\s*embedding/gi, 'Text Embedding')
      .replace(/Multimodal\s*embedding/gi, 'Multimodal Embedding')
      .replace(/Image\s*generation/gi, 'Image Generation')
      .replace(/Text\s*to\s*text/gi, 'Text to Text')
      .replace(/Text\s*to\s*image/gi, 'Text to Image')
      .replace(/Code\s*generation/gi, 'Code Generation')
      // Clean up multiple spaces
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Title case a string (capitalize first letter of each word)
 */
export function toTitleCase(str: string): string {
  if (!str) return '';

  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function applyKnownModelCasing(name: string): string {
  return name
    .replace(/\bDeep\s+Seek\b/g, 'DeepSeek')
    .replace(/\bDeepseek\b/g, 'DeepSeek')
    .replace(/\bMinimax\b/g, 'MiniMax')
    .replace(/\bGpt\b/g, 'GPT')
    .replace(/\bOss\b/g, 'OSS')
    .replace(/\bDbrx\b/g, 'DBRX')
    .replace(/\bGlm\b/g, 'GLM')
    .replace(/\bFp(\d+)\b/g, 'FP$1')
    .replace(/\b([KM])(\d+)p(\d+)\b/g, '$1$2P$3')
    .replace(/\b(\d+)p(\d+)\b/g, '$1P$2')
    .replace(/\bApi\b/g, 'API')
    .replace(/\bAi\b/g, 'AI');
}

/**
 * Full model name formatting with title case
 */
export function formatModelDisplayName(name: string): string {
  return applyKnownModelCasing(toTitleCase(formatModelName(name)));
}
